"use client";

import { FieldWrapper } from "@/components/form/FieldWrapper/FieldWrapper";
import { useId } from "react";
import {
	FieldPath,
	FieldValues,
	RegisterOptions,
	UseFormRegister,
	UseFormRegisterReturn,
	useFormState,
} from "react-hook-form";
import styles from "./InputField.module.scss";

export interface InputFieldProps<T extends FieldValues, TPath extends FieldPath<T>> {
	label: string;
	hideLabel?: boolean;
	className?: string;
	help?: string;
	placeholder?: string;
	register: UseFormRegister<T>;
	name: TPath;
	registerOptions?: RegisterOptions<T, TPath>;
	type?: "text" | "tel" | "email" | "url" | "textarea";
	inputProps?: Omit<
		React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>,
		keyof UseFormRegisterReturn<TPath>
	>;
	slotAfterInput?: React.ReactNode;
	ariaDescribedBy?: string;
	hideError?: boolean;
	rows?: number; // For textarea only
	disabled?: boolean;
	required?: boolean;
}

export const InputField = <T extends FieldValues, TPath extends FieldPath<T>>({
	name,
	label,
	hideLabel,
	className,
	help,
	placeholder,
	register,
	registerOptions,
	inputProps,
	slotAfterInput,
	ariaDescribedBy,
	type = "text",
	hideError,
	rows,
	disabled,
	required,
}: InputFieldProps<T, TPath>) => {
	const id = useId();
	const helpId = id + "-help";
	const errorId = id + "-error";

	const { errors } = useFormState();

	let InputElement: React.ElementType = "input";
	if (type === "textarea") {
		InputElement = "textarea";
	}

	return (
		<FieldWrapper
			name={name}
			label={label}
			hideLabel={hideLabel}
			required={required !== undefined ? required : (registerOptions?.required as boolean)}
			labelFor={id}
			className={className}
			errorId={errorId}
			hideError={hideError}
		>
			<div className={styles["input-wrapper"]}>
				<InputElement
					className={styles["input"]}
					placeholder={placeholder}
					id={id}
					rows={rows}
					type={type}
					disabled={disabled}
					aria-invalid={!!errors[name]}
					aria-required={!!registerOptions?.required}
					aria-describedby={
						[help ? helpId : "", errors[name] ? errorId : "", ariaDescribedBy]
							.filter(v => v)
							.join(" ") || undefined
					}
					{...register(name, registerOptions)}
					{...inputProps}
				/>
				{slotAfterInput}
			</div>
		</FieldWrapper>
	);
};
